import React, { useState, useEffect } from 'react';
import { getServices, saveService, deleteService, uploadFile } from '../../../services/contentApi';

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [imageUploading, setImageUploading] = useState(false);
  const [serviceImage, setServiceImage] = useState('');

  const serviceCategories = [
    { id: 'consultants', name: 'Professional Consultants', icon: '👨‍💼' },
    { id: 'regulatory', name: 'Regulatory/Documentation', icon: '📋' },
    { id: 'tools', name: 'Tools & Calculators', icon: '🧮' },
    { id: 'contractors', name: 'Contractors', icon: '👷' }
  ];

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    if (editingService) {
      setServiceImage(editingService.image || '');
    } else {
      setServiceImage('');
    }
  }, [editingService, showAddModal]);



  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await getServices();
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
      showMessage('Error loading services', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };



  const filteredServices = activeTab === 'all'
    ? services
    : services.filter(service => service.category === activeTab);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImageUploading(true);
      constresult = await uploadFile(file);
      setServiceImage(result.url);
    } catch (error) {
      console.error("Service image upload failed:", error);
      showMessage("Failed to upload image.", 'error');
    } finally {
      setImageUploading(false);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData(e.target);
      const serviceData = {
        name: formData.get('name'),
        category: formData.get('category'),
        description: formData.get('description'),
        specialization: formData.get('specialization') || '',
        price: formData.get('price') || '',
        location: formData.get('location') || 'Pune, Maharashtra',
        tags: formData.get('tags') ? formData.get('tags').split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        status: formData.get('status') || 'active',
        image: serviceImage || '/assets/images/no_image.png',
        isVerified: true
      };

      await saveService(null, serviceData);
      await loadServices();
      setShowAddModal(false);
      showMessage('Service added successfully!');
    } catch (error) {
      console.error('Error adding service:', error);
      showMessage('Error adding service', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditService = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData(e.target);
      const serviceData = {
        name: formData.get('name'),
        category: formData.get('category'),
        description: formData.get('description'),
        specialization: formData.get('specialization') || '',
        price: formData.get('price') || '',
        location: formData.get('location') || 'Pune, Maharashtra',
        tags: formData.get('tags') ? formData.get('tags').split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        status: formData.get('status'),
        image: serviceImage || editingService.image
      };

      await saveService(editingService.id, serviceData);
      await loadServices();
      setEditingService(null);
      showMessage('Service updated successfully!');
    } catch (error) {
      console.error('Error updating service:', error);
      showMessage('Error updating service', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      try {
        await deleteService(serviceId);
        await loadServices();
        showMessage('Service deleted successfully!');
      } catch (error) {
        console.error('Error deleting service:', error);
        showMessage('Error deleting service', 'error');
      }
    }
  };

  const toggleServiceStatus = async (service) => {
    try {
      const newStatus = service.status === 'active' ? 'inactive' : 'active';
      await saveService(service.id, { ...service, status: newStatus });
      await loadServices();
      showMessage(`Service ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling service status:', error);
      showMessage('Error updating service status', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-brown mx-auto mb-4"></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Services Management</h2>
          <p className="text-gray-600">Manage all service offerings and categories</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-custom-brown text-white px-4 py-2 rounded-lg hover:bg-custom-brown/90 transition-colors duration-200 flex items-center space-x-2"
        >
          <span>+</span>
          <span>Add New Service</span>
        </button>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${messageType === 'error'
          ? 'bg-red-100 text-red-700 border border-red-200'
          : 'bg-green-100 text-green-700 border border-green-200'
          }`}>
          <span className="text-lg">{messageType === 'error' ? '❌' : '✅'}</span>
          <span>{message}</span>
        </div>
      )}

      {/* Category Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'all'
              ? 'border-custom-brown text-custom-brown'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            All Services ({services.length})
          </button>
          {serviceCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveTab(category.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === category.id
                ? 'border-custom-brown text-custom-brown'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {category.icon} {category.name} ({services.filter(s => s.category === category.id).length})
            </button>
          ))}
        </nav>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredServices.map((service) => (
          <div key={service.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 bg-gray-100">
              <img
                src={service.image}
                alt={service.name}
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${service.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
                  }`}>
                  {service.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{service.description}</p>
              <p className="text-sm font-medium text-custom-brown mb-4">{service.price}</p>

              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingService(service)}
                  className="flex-1 bg-custom-brown/10 text-custom-brown px-3 py-2 rounded text-sm hover:bg-custom-brown/20 hover:text-custom-brown transition-colors duration-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleServiceStatus(service)}
                  className={`flex-1 px-3 py-2 rounded text-sm transition-colors duration-200 ${service.status === 'active'
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                >
                  {service.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDeleteService(service.id)}
                  className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100 hover:text-red-700 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Service Modal */}
      {(showAddModal || editingService) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false);
              setEditingService(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h3>
            </div>

            <form onSubmit={editingService ? handleEditService : handleAddService} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Service Name
                  </label>
                  <input
                    name="name"
                    type="text"
                    defaultValue={editingService?.name || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Category
                  </label>
                  <select name="category" className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors">
                    {serviceCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows="3"
                    defaultValue={editingService?.description || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Specialization
                  </label>
                  <input
                    name="specialization"
                    type="text"
                    defaultValue={editingService?.specialization || ''}
                    placeholder="e.g., Residential, Commercial, Industrial"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Service Image
                  </label>
                  <div className="flex items-center space-x-4">
                    {serviceImage && (
                      <img src={serviceImage} alt="Preview" className="w-20 h-20 object-cover rounded border border-gray-200" />
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-custom-brown/10 file:text-custom-brown hover:file:bg-custom-brown/20 transition-colors cursor-pointer"
                      />
                    </div>
                  </div>
                  {imageUploading && <p className="text-sm text-custom-brown mt-1">Uploading...</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    name="location"
                    type="text"
                    defaultValue={editingService?.location || 'Pune, Maharashtra'}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Price/Rate
                    </label>
                    <input
                      name="price"
                      type="text"
                      defaultValue={editingService?.price || ''}
                      placeholder="e.g. ₹5,000"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      defaultValue={editingService?.status || 'active'}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    name="tags"
                    type="text"
                    defaultValue={editingService?.tags?.join(', ') || ''}
                    placeholder="e.g., Residential, Commercial, Industrial"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingService(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-custom-brown text-white px-4 py-2 rounded-md hover:bg-custom-brown/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium shadow-sm"
                >
                  {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  <span>{saving ? 'Saving...' : (editingService ? 'Update' : 'Add') + ' Service'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManager; 